import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import "./VoiceLessons.css";
import { voiceContent } from "../content/voiceLessonsContent";

const HERO_IMG =
  "https://mcusercontent.com/f6bfdd76bde1d8b90b791aaa6/_compresseds/b2f081cd-ad76-4f1e-9048-873dba6ab494.jpg";

const TITLE_IMG = "https://i.postimg.cc/43ndG2YD/pytwh-qwl-bbt-ym.png";

const ETHEL_IG = "https://www.instagram.com/ethel_superstarbaby";

const ETHEL_PHOTOS = [
  "https://i.postimg.cc/bwYYHYjG/IMG-9780.avif",
  "https://i.postimg.cc/2j0fRsbM/DSCF8376.jpg",
  "https://i.postimg.cc/52vG0T0K/IMG-6936.avif",
];

// ✅ Google Form embed URLs — replace with real form IDs
const GOOGLE_FORM_HE = "https://docs.google.com/forms/d/e/1FAIpQLSdngFZuOlhb2KvKaR5eTmZYrQDjWBnysUqoM8EWFZxIGpq64A/viewform?embedded=true";
const GOOGLE_FORM_RU = "https://docs.google.com/forms/d/e/1FAIpQLSdGYTEY1zm0aKCpFd5bN6arSHqvfmVUVT7RDhiEQcInXRfrfA/viewform?embedded=true";

function useRevealOnScroll() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) { el.classList.add("isVisible"); return; }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add("isVisible"); obs.unobserve(e.target); }
        });
      },
      { threshold: 0.14 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function useSnapHint(sectionId, hintId) {
  useEffect(() => {
    const section = document.getElementById(sectionId);
    const hint = document.getElementById(hintId);
    if (!section || !hint) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;
    let t = null;
    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        hint.classList.remove("pulse");
        // eslint-disable-next-line no-unused-expressions
        hint.offsetHeight;
        hint.classList.add("pulse");
        if (t) clearTimeout(t);
        t = setTimeout(() => hint.classList.remove("pulse"), 800);
      },
      { threshold: 0.2, rootMargin: "-10% 0px -55% 0px" }
    );
    obs.observe(section);
    return () => { if (t) clearTimeout(t); obs.disconnect(); };
  }, [sectionId, hintId]);
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(0);
  const bodyRef = useRef(null);
  useEffect(() => { if (!bodyRef.current) return; setH(open ? bodyRef.current.scrollHeight : 0); }, [open, a]);
  useEffect(() => {
    const onResize = () => { if (!bodyRef.current) return; if (open) setH(bodyRef.current.scrollHeight); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);
  return (
    <div className={`vfFaqCard ${open ? "open" : ""}`}>
      <button className="vfFaqHead" type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="vfFaqQ">{q}</span>
        <span className={`vfChevron ${open ? "rot" : ""}`} aria-hidden="true">▾</span>
      </button>
      <div className="vfFaqBody" style={{ height: h }}>
        <div ref={bodyRef} className="vfFaqInner">
          <div className="vfFaqA">{a}</div>
        </div>
      </div>
    </div>
  );
}

function EthelCarousel({ alt = "Ethel" }) {
  const [idx, setIdx] = useState(0);
  const scrollerRef = useRef(null);
  const slideWidthRef = useRef(0);
  const userInteractingRef = useRef(false);
  const userTimeoutRef = useRef(null);

  useEffect(() => { ETHEL_PHOTOS.forEach((src) => { const im = new Image(); im.src = src; }); }, []);

  const reduceMotion = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const measure = useCallback(() => { const el = scrollerRef.current; if (!el) return; slideWidthRef.current = el.clientWidth || 0; }, []);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure]);

  const scrollToIndex = useCallback((nextIdx, behavior = "smooth") => {
    const el = scrollerRef.current; if (!el) return;
    const w = slideWidthRef.current || el.clientWidth || 0; if (!w) return;
    el.scrollTo({ left: nextIdx * w, behavior });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current; if (!el) return;
    let raf = null;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const w = slideWidthRef.current || el.clientWidth || 0; if (!w) return;
        const next = Math.round(el.scrollLeft / w);
        setIdx(Math.max(0, Math.min(ETHEL_PHOTOS.length - 1, next)));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { if (raf) cancelAnimationFrame(raf); el.removeEventListener("scroll", onScroll); };
  }, []);

  useEffect(() => {
    const el = scrollerRef.current; if (!el) return;
    const markUser = () => {
      userInteractingRef.current = true;
      if (userTimeoutRef.current) clearTimeout(userTimeoutRef.current);
      userTimeoutRef.current = setTimeout(() => { userInteractingRef.current = false; }, 1600);
    };
    el.addEventListener("touchstart", markUser, { passive: true });
    el.addEventListener("touchmove", markUser, { passive: true });
    el.addEventListener("mousedown", markUser);
    el.addEventListener("mousemove", markUser);
    return () => {
      if (userTimeoutRef.current) clearTimeout(userTimeoutRef.current);
      el.removeEventListener("touchstart", markUser);
      el.removeEventListener("touchmove", markUser);
      el.removeEventListener("mousedown", markUser);
      el.removeEventListener("mousemove", markUser);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    const t = setInterval(() => {
      if (userInteractingRef.current) return;
      const next = (idx + 1) % ETHEL_PHOTOS.length;
      setIdx(next); scrollToIndex(next, "smooth");
    }, 4200);
    return () => clearInterval(t);
  }, [idx, reduceMotion, scrollToIndex]);

  useEffect(() => {
    if (reduceMotion) return;
    scrollToIndex(idx, "smooth");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="vfPhotoWrap" aria-label="Ethel photo carousel">
      <div ref={scrollerRef} className="vfCarouselScroll" role="group" aria-roledescription="carousel" aria-label="Ethel photos">
        {ETHEL_PHOTOS.map((src, i) => (
          <div className="vfCarouselSlide" key={src}>
            <img className="vfCarouselImg" src={src} alt={alt} draggable={false} loading={i === 0 ? "eager" : "lazy"} decoding="async" />
          </div>
        ))}
      </div>
      <div className="vfPhotoTint" aria-hidden="true" />
      <div className="vfPhotoVignette" aria-hidden="true" />
      <div className="vfPhotoDots" aria-hidden="true">
        {ETHEL_PHOTOS.map((_, i) => (
          <button key={i} type="button" className={`vfDotBtn ${i === idx ? "on" : ""}`}
            onClick={() => { setIdx(i); scrollToIndex(i, "smooth"); }} aria-label={`Go to photo ${i + 1}`} />
        ))}
      </div>
    </div>
  );
}

// ✅ Google Form embed — language-specific
function GoogleFormEmbed({ lang }) {
  const formUrl = lang === "he" ? GOOGLE_FORM_HE : GOOGLE_FORM_RU;
  const isPlaceholder = formUrl.includes("YOUR_");

  if (isPlaceholder) {
    return (
      <div className="vfFormPlaceholder">
        <div className="vfFormPlaceholderIcon">✦</div>
        <p className="vfFormPlaceholderText">
          {lang === "he"
            ? "טופס הגוגל יופיע כאן — עדכן את GOOGLE_FORM_HE ב-VoiceLessons.jsx"
            : "Форма Google появится здесь — обновите GOOGLE_FORM_RU в VoiceLessons.jsx"}
        </p>
      </div>
    );
  }

  return (
    <div className="vfGoogleFormBox">
      <iframe
        src={formUrl}
        title={lang === "he" ? "טופס הרשמה" : "Форма заявки"}
        frameBorder="0"
        marginHeight="0"
        marginWidth="0"
        className="vfGoogleFormFrame"
      >
        Loading…
      </iframe>
    </div>
  );
}

export default function VoiceLessons({ lang: propLang }) {
  // If lang is passed as prop (from route), lock to that language
  const [lang, setLang] = useState(propLang || "he");
  const isLocked = !!propLang; // when routed to /voice-ru, lock language
  const t = useMemo(() => voiceContent[lang], [lang]);
  const isHebrew = lang === "he";

  // Sync if prop changes
  useEffect(() => { if (propLang) setLang(propLang); }, [propLang]);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);

  const WHATSAPP_NUMBER = "972549145855";
  const whatsappMsg = lang === "he"
    ? "היי, אני מעוניין/ת בשיעורי פיתוח קול"
    : "Привет, я хочу записаться на уроки вокала";
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMsg)}`;

  const openPopup = () => setShowPopup(true);
  const closePopup = () => setShowPopup(false);

  const goToForm = () => {
    closePopup();
    setTimeout(() => {
      const el = document.getElementById("form-area");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  };

  const scrollToForm = goToForm; // keep existing references working

  const scrollToHow = () => {
    const el = document.getElementById("how-it-works");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => { const img = new Image(); img.src = HERO_IMG; }, []);

  const r1 = useRevealOnScroll();
  const r2 = useRevealOnScroll();
  const r3 = useRevealOnScroll();
  const r4 = useRevealOnScroll();
  const r5 = useRevealOnScroll();
  const r6 = useRevealOnScroll();

  useSnapHint("sec-2", "hint-1");
  useSnapHint("how-it-works", "hint-2");
  useSnapHint("sec-4", "hint-3");
  useSnapHint("sec-5", "hint-4");
  useSnapHint("form-area", "hint-5");

  const limitedSlots = lang === "he" ? "מספר מקומות שבועיים מוגבל" : "Количество мест в неделю ограничено";

  // Stars from the flyer
  const Stars = () => (
    <div className="vfStars" aria-hidden="true">
      <span>★</span><span>★</span><span>★</span>
    </div>
  );

  return (
    <div className={`voiceFunnel ${isHebrew ? "rtl" : "ltr"}`}>
      {/* Top bar */}
      <div className="vfTop">
        <div className="vfLang">
          {!isLocked ? (
            <>
              <button className={`vfLangBtn ${lang === "he" ? "active" : ""}`} onClick={() => setLang("he")} type="button">עברית</button>
              <span className="vfSep">|</span>
              <button className={`vfLangBtn ${lang === "ru" ? "active" : ""}`} onClick={() => setLang("ru")} type="button">Русский</button>
            </>
          ) : (
            <span className="vfLangLocked">{lang === "he" ? "עברית" : "Русский"}</span>
          )}
        </div>
        <button className="vfTopCta" onClick={scrollToForm} type="button">{t.ctaPrimary}</button>
      </div>

      {/* Hero — image + title only */}
      <section className="vfHero">
        <div className="vfHeroImg" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="vfHeroOverlay" />

        <div className="vfHeroShelf heroEnter">
          {lang === "he" ? (
            <img className="vfTitleImg" src={TITLE_IMG} alt={t.title} loading="eager" draggable="false" />
          ) : (
            <h1 className="vfTitleText">{t.title}</h1>
          )}
        </div>
      </section>

      {/* Content block — seamlessly below hero, same dark bg */}
      <div className="vfHeroContent">
        <Stars />
        <p className="vfSubtitle">{t.subtitle}</p>
        <div className="vfPills">
          {t.bulletsTop.map((b, i) => (
            <span key={i} className="vfPill">{b}</span>
          ))}
        </div>
        <div className="vfHeroBtns">
          <button className="vfBtnPrimary" onClick={scrollToForm} type="button">{t.ctaPrimary}</button>
          <button className="vfBtnGhost" onClick={scrollToHow} type="button">{t.ctaSecondary}</button>
        </div>
        <div className="vfLimited">{limitedSlots}</div>
      </div>

      {/* Content */}
      <main className="vfWrap">
        <section ref={r1} className="vfSection accentPink" id="sec-1">
          <div className="vfKicker">{t.kicker1}</div>
          <h2 className="vfH2 gradientTitle">{t.section1Title}</h2>
          <p className="vfText preline">{t.section1Text}</p>
        </section>

        <div className="vfSnapHint" id="hint-1" aria-hidden="true" />

        <section ref={r2} className="vfSection accentPurple" id="sec-2">
          <div className="vfKicker">{t.kicker2}</div>
          <h2 className="vfH2 gradientTitle">{t.section2Title}</h2>
          <ul className="vfList">
            {t.section2List.map((x, i) => (<li key={i}>{x}</li>))}
          </ul>
        </section>

        <div className="vfSnapHint" id="hint-2" aria-hidden="true" />

        <section ref={r3} className="vfSection accentBlue" id="how-it-works">
          <div className="vfKicker">{t.kicker3}</div>
          <h2 className="vfH2 gradientTitle">{t.section3Title}</h2>
          <div className="vfSteps">
            {t.section3Steps.map((s, i) => (
              <div key={i} className="vfStep">
                <div className="vfStepNum">{i + 1}</div>
                <div className="vfStepTitle">{s.title}</div>
                <div className="vfStepText">{s.text}</div>
              </div>
            ))}
          </div>
        </section>

        <div className="vfSnapHint" id="hint-3" aria-hidden="true" />

        <section ref={r4} className="vfSection accentPink" id="sec-4">
          <div className="vfKicker">{t.kicker4}</div>
          <h2 className="vfH2 gradientTitle">{t.section4Title}</h2>
          <div className="vfAboutName">{t.section4Name}</div>
          <p className="vfText preline">{t.section4Text}</p>
          <EthelCarousel alt={t.section4Name} />
          <a className="vfIgBanner" href={ETHEL_IG} target="_blank" rel="noopener noreferrer" aria-label="Instagram: ethel_superstarbaby" title="Instagram">
            <span className="vfIgIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm5 5.8A4.2 4.2 0 1 0 16.2 12 4.2 4.2 0 0 0 12 7.8zm6.4-.9a1 1 0 1 0 1 1 1 1 0 0 0-1-1z" /></svg>
            </span>
            <span className="vfIgText">@ethel_superstarbaby</span>
            <span className="vfIgHint" aria-hidden="true">↗</span>
          </a>
        </section>

        <div className="vfSnapHint" id="hint-4" aria-hidden="true" />

        <section ref={r5} className="vfSection accentPurple" id="sec-5">
          <div className="vfKicker">{t.kicker5}</div>
          <h2 className="vfH2 gradientTitle">{t.faqTitle}</h2>
          <div className="vfFaq">
            {t.faq.map((item, i) => (<FaqItem key={i} q={item.q} a={item.a} />))}
          </div>
        </section>

        <div className="vfSnapHint" id="hint-5" aria-hidden="true" />

        {/* ✅ GOOGLE FORM — language-specific */}
        <section ref={r6} className="vfSection accentBlue" id="form-area">
          <div className="vfKicker">{t.kicker6}</div>
          <h2 className="vfH2 gradientTitle">{t.formTitle}</h2>
          <GoogleFormEmbed lang={lang} />
        </section>

        <footer className="vfFooter">
          <div className="vfFooterStars" aria-hidden="true">★ ★ ★</div>
          {t.footer}
        </footer>
      </main>

      {/* Bottom sticky CTA */}
      <div className="vfSticky">
        <button className="vfStickyBtn" onClick={openPopup} type="button">{t.ctaPrimary}</button>
        <div className="vfStickySub">{limitedSlots}</div>
      </div>

      {/* ✦ Contact popup */}
      {showPopup && (
        <div className="vfPopupOverlay" onClick={closePopup} aria-modal="true" role="dialog">
          <div className="vfPopup" onClick={e => e.stopPropagation()}>
            <button className="vfPopupClose" onClick={closePopup} type="button" aria-label="סגור">✕</button>

            <div className="vfPopupTitle">
              {lang === "he" ? "איך להתחיל?" : "Как начать?"}
            </div>
            <div className="vfPopupSub">
              {lang === "he"
                ? "בחרו את הדרך שנוחה לכם"
                : "Выберите удобный способ"}
            </div>

            <div className="vfPopupBtns">
              {/* WhatsApp */}
              <a
                className="vfPopupWhatsapp"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closePopup}
              >
                <span className="vfPopupBtnIcon">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.124.558 4.17 1.535 5.943L.057 23.886a.5.5 0 0 0 .613.614l6.012-1.467A11.953 11.953 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.88 9.88 0 0 1-5.031-1.378l-.36-.214-3.733.91.944-3.641-.235-.374A9.873 9.873 0 0 1 2.118 12C2.118 6.533 6.533 2.118 12 2.118S21.882 6.533 21.882 12 17.467 21.882 12 21.882z"/>
                  </svg>
                </span>
                <span className="vfPopupBtnText">
                  <span className="vfPopupBtnLabel">WhatsApp</span>
                  <span className="vfPopupBtnDesc">
                    {lang === "he" ? "שלחו הודעה עכשיו" : "Написать сейчас"}
                  </span>
                </span>
              </a>

              {/* Form */}
              <button className="vfPopupForm" onClick={goToForm} type="button">
                <span className="vfPopupBtnIcon">✦</span>
                <span className="vfPopupBtnText">
                  <span className="vfPopupBtnLabel">
                    {lang === "he" ? "טופס פרטים" : "Форма заявки"}
                  </span>
                  <span className="vfPopupBtnDesc">
                    {lang === "he" ? "השאירו פרטים ונחזור אליכם" : "Оставьте контакты"}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
